import type { TOC } from '@ember/component/template-only';
import { PrismButton, PrismLoadingBar } from '@mylighthouse/prism-ember';
import { t } from 'ember-intl';
import GeneralDatepickersDatepickerRange from 'lumen/components/general/datepickers/datepicker-range';
import GeneralDropdownsDropdownExport from 'lumen/components/general/dropdowns/dropdown-export';
import GeneralPageHeader from 'lumen/components/general/page/header';
import GeneralPageMain from 'lumen/components/general/page/main';
import HotelEventsCalendarsCalendarEvents from 'lumen/components/hotel/events/calendars/calendar-events';
import HotelEventsLegendEvents from 'lumen/components/hotel/events/legend-events';
import HotelEventsSelectionBarFilters from 'lumen/components/hotel/events/selection-bar-filters';
import HotelAndMarketDropdownsAccountSelectorWrapper from 'lumen/components/hotel-and-market/dropdowns/account-selector-wrapper';
import type HotelEventsController from 'lumen/controllers/hotel/events';
import can from 'lumen/helpers/can';
import { HEADER_HEIGHT } from 'lumen/utils/constants/general-constants';

interface HotelEventsSignature {
  Args: {
    controller: HotelEventsController;
    model: unknown;
  };
}

// Reserve space for the 2 main headers + main content padding bottom + main content padding top + calender header + legend
const CALENDAR_RESERVED_HEIGHT = HEADER_HEIGHT + 16 + 8 + 30 + 24;

<template>
  <GeneralPageHeader>
    <div class="content-wrapper flex flex-row justify-between">
      <div class="flex gap-400">
        <HotelAndMarketDropdownsAccountSelectorWrapper @hasDivider={{true}} />
        <GeneralDatepickersDatepickerRange
          @displayEvents={{true}}
          {{! @glint-expect-error: Incorrect type }}
          @range={{@controller.dateRange}}
          @rangeChanged={{@controller.updateSelectedMonth}}
          @selectStandardRange={{true}}
          @standardRange="month"
        />
      </div>
      <div class="flex flex-row items-center gap-200">
        {{! @glint-expect-error: Incorrect type }}
        <GeneralDropdownsDropdownExport
          @export={{@controller.exportEvents}}
          @exportChoices={{@controller.exportChoices}}
        />
        {{#if (can "events_holidays.author")}}
          <PrismButton
            @buttonType="primary"
            @iconBefore="plus"
            @label={{t "events.add_event"}}
            @onClick={{@controller.addEvent}}
          />
        {{/if}}
      </div>
    </div>
  </GeneralPageHeader>

  <GeneralPageHeader @isSmall={{true}}>
    <div class="content-wrapper flex items-center pt-200 pb-200">
      <HotelEventsSelectionBarFilters
        @addExistingEvents={{@controller.addExistingEvents}}
        @fetchSearchResult={{@controller.fetchSearchResult}}
        @resetEvents={{@controller.resetEvents}}
        @searchResults={{@controller.searchResults}}
        @selectedEventsCategories={{@controller.eventsCategories}}
        @selectedEventsStatuses={{@controller.eventsStatuses}}
        @selectionsChanged={{@controller.selectionsChanged}}
      />
    </div>
  </GeneralPageHeader>

  <GeneralPageMain class="events-calendar-layout">
    <div class="content-wrapper main-content--new-layout">
      {{#if @controller.isLoading}}
        <PrismLoadingBar @isFixed={{true}} @positionTop={{120}} />
      {{/if}}

      <HotelEventsLegendEvents />

      <HotelEventsCalendarsCalendarEvents
        @addEvent={{@controller.addEventForDate}}
        @containerHeight={{@controller.containerHeight}}
        @isLoading={{@controller.isLoading}}
        @month={{@controller.month}}
        @monthMoment={{@controller.monthMoment}}
        @periods={{@controller.periods}}
        @refreshEvents={{@controller.resetEvents}}
        @reservedHeight={{CALENDAR_RESERVED_HEIGHT}}
        @selectedEventsCategories={{@controller.eventsCategories}}
        @selectedEventsStatuses={{@controller.eventsStatuses}}
      />
    </div>
  </GeneralPageMain>

  {{outlet}}
</template> satisfies TOC<HotelEventsSignature>;
